import React, { useEffect, useState } from 'react';
import { Chart } from 'primereact/chart';
import { Calendar } from 'primereact/calendar';
import car from '../../assets/images/car.png';
import user from '../../assets/images/users.png';
import dollar from '../../assets/images/dollar.png';
import time from '../../assets/images/time.png';
import { handleGetRequest } from '../../services/GetTemplate';

function DashboardSection({ title, cardsData, chartData, chartOptions, chartDataa, lightOptions }) {
    return (
        <>
            {title && <h2 style={{ fontWeight: '600', color: '#000', marginBottom: '30px' }}>{title}</h2>}

            <div className="cards__girds col-12">
                {cardsData?.map((card, index) => (
                    <div key={index} className="dashboard-cards p-3">
                        <div className="p-3 mb-4 round-sub-card">
                            <img src={card.icon} alt={card.title} />
                        </div>
                        <p className="card-heading">{card.title}</p>
                        <p className="card-sub-text">{card.value}</p>
                    </div>
                ))}
            </div>

            {chartData && (
                <div className="graphs__section" style={{ marginTop: '80px' }}>
                    <div className="Card__Round p-4" style={{ border: 'none' }}>
                        <h3>Weekly Revenue</h3>
                        <Chart type="line" data={chartData} options={chartOptions} />
                    </div>

                    {/* <div className="Card__Round p-4" style={{ border: 'none', marginTop: '40px' }}>
                        <h3>Space Distribution</h3>
                        <Chart type="doughnut" data={chartDataa} options={lightOptions} height="500px" />
                    </div> */}
                </div>
            )}
        </>
    );
}

function CustomDashboard() {
    const [weeklyData, setWeeklyData] = useState([]);
    const [distributionData, setDistributionData] = useState(null);
    const [cardsData, setCardsData] = useState({});
    const [enforcementData, setEnforcementData] = useState({});
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const [chartDataa, setChartDataa] = useState({
        labels: ['Occupied', 'Available', 'Total Capacity'],
        datasets: [
            {
                data: [0, 0, 0],
                backgroundColor: ['#2D8EFF', '#8B5CF6', '#10B981']
            }
        ]
    });

    const lightOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' }
        }
    };

    // Separate start and end date for main dashboard
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    // Independent dates for enforcement dashboard (so changing main dates doesn't affect enforcement)
    const [enfStartDate, setEnfStartDate] = useState(null);
    const [enfEndDate, setEnfEndDate] = useState(null);

    // Update charts
    useEffect(() => {
        setChartData({
            labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            datasets: [
                {
                    label: 'Revenue',
                    data: weeklyData,
                    borderColor: '#2D8EFF',
                    tension: 0.4
                }
            ]
        });

        setChartOptions({ maintainAspectRatio: false });
    }, [weeklyData]);

    useEffect(() => {
        if (distributionData) {
            setChartDataa({
                labels: ['Occupied', 'Available', 'Total Capacity'],
                datasets: [
                    {
                        data: [distributionData.occupied || 0, distributionData.availableSlots || 0, distributionData.totalCapacity || 0],
                        backgroundColor: ['#2D8EFF', '#8B5CF6', '#10B981']
                    }
                ]
            });
        }
    }, [distributionData]);

    // Helper to format dates for API — return full ISO date-time as Swagger expects string($date-time)
    const formatDate = (date) => (date ? date.toISOString() : '');
    // Parse ISO date-time string from API into a Date object
    const parseDate = (iso) => {
        try {
            return iso ? new Date(iso) : null;
        } catch (e) {
            return null;
        }
    };

    // API calls with startDate and endDate
    const getWeeklyData = async () => {
        try {
            let url = 'portalDashboard/GetWeeklyRevenue';
            if (startDate && endDate) url += `?fromDate=${encodeURIComponent(formatDate(startDate))}&toDate=${encodeURIComponent(formatDate(endDate))}`;
            const res = await handleGetRequest(url, false);
            if (res?.success) {
                // support multiple shapes: res.data.revenue or res.revenue or res.data
                const revenueRaw = res.data?.revenue ?? res.revenue ?? res.data ?? res;
                const revenue = Array.isArray(revenueRaw) ? revenueRaw : revenueRaw?.revenue ?? [];
                setWeeklyData(revenue);
                const from = res.data?.fromDate ?? res.fromDate ?? (res.data && res.data.fromDate);
                const to = res.data?.toDate ?? res.toDate ?? (res.data && res.data.toDate);
                if (!startDate && from) setStartDate(parseDate(from));
                if (!endDate && to) setEndDate(parseDate(to));
            }
        } catch (err) {
            console.error('Failed to fetch weekly data:', err);
            setWeeklyData([]);
        }
    };

    const getRevenueData = async () => {
        try {
            let url = 'portalDashboard/GetTotalCapacityPieChart';
            if (startDate && endDate) url += `?fromDate=${encodeURIComponent(formatDate(startDate))}&toDate=${encodeURIComponent(formatDate(endDate))}`;
            const res = await handleGetRequest(url, false);
            if (res?.success) {
                const payload = res.data ?? res;
                setDistributionData(payload || { occupied: 0, availableSlots: 0, totalCapacity: 0 });
                const from = payload?.fromDate ?? res.fromDate;
                const to = payload?.toDate ?? res.toDate;
                if (!startDate && from) setStartDate(parseDate(from));
                if (!endDate && to) setEndDate(parseDate(to));
            }
        } catch (err) {
            console.error('Failed to fetch distribution data:', err);
            setDistributionData({ occupied: 0, availableSlots: 0, totalCapacity: 0 });
        }
    };

    const getCardsData = async () => {
        try {
            let url = 'portalDashboard/GetDashboardSummary';
            if (startDate && endDate) url += `?fromDate=${encodeURIComponent(formatDate(startDate))}&toDate=${encodeURIComponent(formatDate(endDate))}`;
            const res = await handleGetRequest(url, false);
            if (res?.success) {
                const payload = res.data ?? res;
                setCardsData(payload || {});
                const from = payload?.fromDate ?? res.fromDate;
                const to = payload?.toDate ?? res.toDate;
                if (!startDate && from) setStartDate(parseDate(from));
                if (!endDate && to) setEndDate(parseDate(to));
            }
        } catch (err) {
            console.error('Failed to fetch main dashboard cards:', err);
            setCardsData({});
        }
    };

    const getEnforcementData = async () => {
        try {
            let url = 'portalDashboard/EnforcementDashboard';
            if (enfStartDate && enfEndDate) url += `?fromDate=${encodeURIComponent(formatDate(enfStartDate))}&toDate=${encodeURIComponent(formatDate(enfEndDate))}`;
            const res = await handleGetRequest(url, false);
            if (res?.success) {
                const payload = res.data ?? res;
                setEnforcementData(payload || {});
                const from = payload?.fromDate ?? res.fromDate;
                const to = payload?.toDate ?? res.toDate;
                if (!enfStartDate && from) setEnfStartDate(parseDate(from));
                if (!enfEndDate && to) setEnfEndDate(parseDate(to));
            }
        } catch (err) {
            console.error('Failed to fetch Enforcement dashboard cards:', err);
            setEnforcementData({});
        }
    };

    // const getEnforcementData = async () => {
    //    // try {
    //         // // let url = 'portalDashboard/GetEnforcementSummary';
    //         // if (startDate && endDate) url += `?startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}`;
    //     //     const res = await handleGetRequest(url, false);
    //     //     if (res?.success) setEnforcementData(res.data || {});
    //     //     else setEnforcementData({});
    //     // } catch (err) {
    //     //     console.error('Failed to fetch enforcement data:', err);
    //     //     setEnforcementData({});
    //     }
    // };

    // Initial load for main dashboard
    useEffect(() => {
        getWeeklyData();
        getRevenueData();
        getCardsData();
    }, [startDate, endDate]);

    // Initial and reactive load for enforcement dashboard (independent of main dashboard dates)
    useEffect(() => {
        getEnforcementData();
    }, [enfStartDate, enfEndDate]);

    const enforcementCards = [
        { title: 'Overstay', value: enforcementData?.overStay || 0, icon: time },
        { title: 'Non-Payment', value: enforcementData?.nonPayment || 0, icon: car },
        { title: 'Fine Amount', value: enforcementData?.amount || 0, icon: car },
        { title: 'Fine Count', value: enforcementData?.fineCount || 0, icon: time }
        // { title: 'Amount', value: enforcementData?.finesAmount || 0, icon: dollar }
    ];

    const mainDashboardCards = [
        // { title: 'Total Space', value: cardsData?.totalSpaces || 0, icon: car },
        // { title: 'Active Users', value: cardsData?.activeUsers || 0, icon: user },
        { title: 'Total Vehicles', value: cardsData?.registeredVehicles || 0, icon: car },
        { title: 'Revenue Today', value: cardsData?.revenueToday || 0, icon: dollar },
        { title: 'Average Parking Time', value: cardsData?.avgDuration || 0, icon: time },
        { title: 'Car Parked', value: cardsData?.parkedCars || 0, icon: car }
    ];

    const today = React.useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);



    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ fontWeight: '600', color: '#000' }}>Main Dashboard</h2>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Calendar
                        onChange={(e) => {
                            setStartDate(e.value);
                            if (endDate && e.value && endDate < e.value) {
                                setEndDate(null);
                            }
                        }}
                        maxDate={today}
                        value={startDate}
                        placeholder="Start Date"
                        dateFormat="yy-mm-dd"
                        style={{ width: '150px' }}
                    />
                    <Calendar maxDate={today} onChange={(e) => setEndDate(e.value)} minDate={startDate} value={endDate} placeholder="End Date" dateFormat="yy-mm-dd" style={{ width: '150px' }} />
                </div>
            </div>

            <DashboardSection cardsData={mainDashboardCards} chartData={chartData} chartOptions={chartOptions} chartDataa={chartDataa} lightOptions={lightOptions} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', marginTop: '150px' }}>
                <h2 style={{ fontWeight: '600', color: '#000' }}>Enforcement Dashboard</h2>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Calendar
                        maxDate={today}
                        value={enfStartDate}
                        onChange={(e) => {
                            setEnfStartDate(e.value);
                            if (enfEndDate && e.value && enfEndDate < e.value) {
                                setEnfEndDate(null);
                            }
                        }}
                        placeholder="Start Date"
                        dateFormat="yy-mm-dd"
                        style={{ width: '150px' }}
                    />
                    <Calendar value={enfEndDate} onChange={(e) => setEnfEndDate(e.value)} maxDate={today} minDate={enfStartDate} placeholder="End Date" dateFormat="yy-mm-dd" style={{ width: '150px' }} />
                </div>
            </div>

            <div>
                <DashboardSection cardsData={enforcementCards} chartData={chartData} chartOptions={chartOptions} chartDataa={chartDataa} lightOptions={lightOptions} />
            </div>
        </>
    );
}

export default CustomDashboard;
